import React from 'react';
import { useAppSelector, useAppDispatch } from '../../../../app/hooks';
import { TpDataContext, TpDataTable, TpDataTableColumn, VALTYPES } from '../../../../data/interfaces';
import {
  TpExternalDataResult,
  EXTERNAL_DATA_STATUS,
  selectExternalData,
} from '../../../../externalData/externalDataSlice';
import { getExternalData } from '../../../../externalData/getExternalData';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import styles from '../SingleItemView.module.scss';
import { SingleEntityTemplate } from '../../../../api/models/assets';
import { getDataTable } from '../../charts/chartDataBindings';
import { getGroupInfo } from '../../../../data/analyticsDataSlice';
import { SectionIndex, TpSectionsIndexContext } from '../../../../components/sections-index/SectionsIndex';
import { getUserError } from '../../../../components/miscUtils';

function renderExternalData(request: TpExternalDataResult) {
  if (request.status == EXTERNAL_DATA_STATUS.PENDING) return <div>Pending...</div>;
  if (request.status == EXTERNAL_DATA_STATUS.FAILED) return <div>Failed</div>;
  return <div>{request.content}</div>;
}

function isEmptyDataValue(colInfo: TpDataTableColumn, value: any): boolean {
  if (colInfo.valueType == VALTYPES.INTEGER || colInfo.valueType == VALTYPES.NUMBER)
    return isNaN(value) || value === null;
  if (colInfo.valueType == VALTYPES.CATEGORICAL) return !convertColumnDataToDisplayString(colInfo, value);
  if (colInfo.valueType == VALTYPES.MULTICAT || colInfo.valueType == VALTYPES.MULTISTRING)
    return !value || value.length == 0;
  if (colInfo.valueType == VALTYPES.STRING) return !value;
  return false;
}

interface TpPropConfig {
  id: string;
  useBadge?: boolean;
}

interface TpElementTemplate {
  title: string;
  dataTableId?: string;
  propertyGroupIds?: string[];
  hideAbsentProperties?: boolean;
  propsToShow?: string[];
  propConfig?: TpPropConfig[];
}

interface TpTableProps {
  itemId: string;
  dataContext: TpDataContext;
  entityTypeTemplate?: SingleEntityTemplate;
  children?: React.ReactNode;
  elementTemplate: any;
  sectionIndexContext: TpSectionsIndexContext;
}

const Tables = (props: TpTableProps) => {
  const { dataContext } = props;
  const canonicalTypeId = props.entityTypeTemplate!.entityType;
  const externalData = useAppSelector(selectExternalData);

  const dispatch = useAppDispatch();

  const elementTemplate = props.elementTemplate as TpElementTemplate;

  const hideAbsentProperties = elementTemplate.hideAbsentProperties || false;
  const propConfigs = elementTemplate.propConfig || [];

  function renderField(dataTable: TpDataTable, column: TpDataTableColumn, itemIndex: number) {
    if (hideAbsentProperties && isEmptyDataValue(column, dataTable.columnData[column.id][itemIndex])) return null;
    if (propConfigs.length > 0) {
      const propConfig = propConfigs.find((config) => config.id == column.id);
      if (propConfig && propConfig.useBadge) {
        const displayString = convertColumnDataToDisplayString(column, dataTable.columnData[column.id][itemIndex]);
        if (displayString !== '') {
          return (
            <div key={column.id}>
              <div className={styles.fieldLabel}>{column.name}</div>
              <div className={styles.fieldValue}>
                {displayString.split('; ').map((value, index) => (
                  <span key={`badge_${index}`} className={styles.badge}>
                    {value}
                  </span>
                ))}
              </div>
            </div>
          );
        }
      }
    }
    return (
      <div key={column.id}>
        <div className={styles.fieldLabel}>{column.name}</div>
        <div className={styles.fieldValue}>
          {convertColumnDataToDisplayString(column, dataTable.columnData[column.id][itemIndex])}
        </div>
      </div>
    );
  }

  if (elementTemplate.propertyGroupIds) {
    // We render a selected number of property groups fron a single table
    const sectionId = 'section_' + elementTemplate.propertyGroupIds.join('_');
    if (!elementTemplate.dataTableId)
      throw getUserError('An entity table with groups specified should also specify a data table');
    const dataTable = getDataTable(dataContext, elementTemplate.dataTableId);
    const itemIndex = dataTable.itemIdMap[props.itemId];
    let groups: any = elementTemplate.propertyGroupIds.map((groupId) => (
      <React.Fragment key={groupId}>
        <div className={styles.fieldGroup}>{getGroupInfo(dataTable, groupId).name}</div>
        {dataTable.columns
          .filter((column) => column.groupId == groupId)
          .map((column: TpDataTableColumn) => renderField(dataTable, column, itemIndex))}
      </React.Fragment>
    ));
    if (elementTemplate.propertyGroupIds.length == 0) {
      // no group specified => we collect all props that do not belong to a group
      groups = dataTable.columns
        .filter((column) => !column.groupId)
        .map((column: TpDataTableColumn) => renderField(dataTable, column, itemIndex));
    }
    return (
      <SectionIndex
        key={sectionId}
        sectionIndexContext={props.sectionIndexContext}
        sectionId={`table_group_${dataTable.id}_${sectionId}`}
        sectionName={elementTemplate.title}
      >
        <div className={styles.tableTitle}>{elementTemplate.title}</div>
        {groups}
      </SectionIndex>
    );
  }

  // We render all properties applicable to this entity
  const tables = props.dataContext.dataTables
    .filter(
      (dataTable) => dataTable.canonicalTypeId == canonicalTypeId && dataTable.itemIdMap[props.itemId] !== undefined,
    )
    .map((dataTable) => {
      const itemIndex = dataTable.itemIdMap[props.itemId];
      let rows;
      if (dataTable.columnGroups.length == 0) {
        rows = dataTable.columns
          .filter(
            (col) =>
              !elementTemplate.propsToShow ||
              (elementTemplate.propsToShow && elementTemplate.propsToShow.includes(col.id)),
          )
          .sort((a, b) => {
            if (!elementTemplate.propsToShow) {
              return 0;
            }
            return elementTemplate.propsToShow.indexOf(a.id) - elementTemplate.propsToShow.indexOf(b.id);
          })
          .map((column: TpDataTableColumn) => renderField(dataTable, column, itemIndex));
      } else {
        // We first already generate all the fields that will be rendered and combine that with the columnGroups
        const processedGroups = dataTable.columnGroups.map((group) => {
          const processedColumns = dataTable.columns
            .filter((column) => column.groupId == group.id)
            .filter(
              (col) =>
                !elementTemplate.propsToShow ||
                (elementTemplate.propsToShow && elementTemplate.propsToShow.includes(col.id)),
            )
            .sort((a, b) => {
              if (!elementTemplate.propsToShow) {
                return 0;
              }
              return elementTemplate.propsToShow.indexOf(a.id) - elementTemplate.propsToShow.indexOf(b.id);
            })
            .map((column: TpDataTableColumn) => renderField(dataTable, column, itemIndex));
          return { ...group, columns: processedColumns };
        });

        // Now we make sure we only visualize the propertygroups where at least one field is present
        rows = processedGroups
          .filter((pg) => pg.columns.some((column) => !!column))
          .map((group) => {
            return (
              <React.Fragment key={group.id}>
                <div className={styles.fieldGroup}>{group.name}</div>
                {group.columns}
              </React.Fragment>
            );
          });
        rows.unshift(
          <React.Fragment key="nogroup">
            {dataTable.columns
              .filter((column) => !column.groupId)
              .filter(
                (col) =>
                  !elementTemplate.propsToShow ||
                  (elementTemplate.propsToShow && elementTemplate.propsToShow.includes(col.id)),
              )
              .sort((a, b) => {
                if (!elementTemplate.propsToShow) {
                  return 0;
                }
                return elementTemplate.propsToShow.indexOf(a.id) - elementTemplate.propsToShow.indexOf(b.id);
              })
              .map((column: TpDataTableColumn) => renderField(dataTable, column, itemIndex))}
          </React.Fragment>,
        );
      }
      return (
        <React.Fragment key={dataTable.id}>
          <div className={styles.tableTitle}>{dataTable.name}</div>
          {rows}
        </React.Fragment>
      );
    });

  if (canonicalTypeId == 'demo_bioreflect_result') {
    // @doto: move this hardwired logic into a generic, plugin-like architecture
    const resp = getExternalData('publicationAbstract', { pmid: props.itemId }, externalData, dispatch);
    tables.push(
      <div key="abstract">
        <div className={styles.fieldGroup}>Abstract</div>
        {renderExternalData(resp)}
      </div>,
    );
  }

  return <>{tables}</>;
};

export default Tables;
